import type { HttpEvent, HttpInterceptor } from '@angular/common/http';

import { Injectable } from '@angular/core';
import { type HttpRequest, type HttpHandler, HttpErrorResponse } from '@angular/common/http';
import { throwError, type Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

@Injectable()
export abstract class BaseJwtInterceptor implements HttpInterceptor {
    protected readonly accessToken: string = 'oauth_access_token';
    protected readonly adminAccessToken: string = 'admin_access_token';
    protected token: string;
    protected isAdmin: boolean;

    protected readonly skipAuthUrls: string[] = [
        'https://vimeo.com/api/',
        'https://api.openai.com/v1/',
        'https://geoip.sendpulse.com',
    ];
    protected readonly isDev: boolean = false;

    protected abstract unauthorizedAction(): void;
    protected abstract getToken(request: HttpRequest<unknown>): string;

    public intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        if (this.isAdmin && !request.url.includes('/admin/')) {
            return throwError(
                () =>
                    new HttpErrorResponse({
                        status: 400,
                        statusText: 'Blocked by interceptor',
                        url: request.url,
                        error: { code: 'NON_ADMIN_REQUEST' },
                    })
            );
        }

        const token = this.getToken(request);
        if (token && !this.urlIsInSkipAuthUrls(request.url)) {
            request = request.clone({
                setHeaders: {
                    Authorization: `Bearer ${token}`,
                },
            });
        }

        return next.handle(request).pipe(
            tap(
                (event: any) => event,
                (error: any) => {
                    if (error instanceof HttpErrorResponse && error.status === 401 && !this.isDev) {
                        this.unauthorizedAction();
                    }
                }
            )
        );
    }

    private urlIsInSkipAuthUrls(url: string): boolean {
        return this.skipAuthUrls.some((skipUrl) => url.includes(skipUrl));
    }
}
